(() => {
  if (window.afbCartQuantityCapInstalled) return;
  window.afbCartQuantityCapInstalled = true;

  const nativeFetch = window.fetch.bind(window);
  let enforcementPromise = null;
  let noticeTimer = null;

  const BUNDLE_HANDLE = 'campaign-pack-bundle-all-three';
  const BUNDLE_COMPONENTS = {
    'campaign-pack-ecommerce-retail': {
      name: 'E-commerce & Retail',
      variantIds: ['52823666786585'],
    },
    'campaign-pack-real-estate-property-management': {
      name: 'Real Estate & Property Management',
      variantIds: ['52823667933465'],
    },
    'campaign-pack-restaurants-hospitality': {
      name: 'Restaurants & Hospitality',
      variantIds: ['52823667376409'],
    },
  };
  const BUNDLE_COMPONENT_HANDLES = Object.keys(BUNDLE_COMPONENTS);
  const COMPONENT_BY_VARIANT_ID = Object.entries(BUNDLE_COMPONENTS).reduce((map, [handle, component]) => {
    component.variantIds.forEach((variantId) => map.set(String(variantId), { handle, name: component.name }));
    return map;
  }, new Map());

  const routeWithJson = (route) => (route.endsWith('.js') ? route : `${route}.js`);
  const normalizePath = (resource) => {
    const rawUrl = typeof resource === 'string' ? resource : resource?.url;
    if (!rawUrl) return '';

    try {
      return new URL(rawUrl, window.location.origin).pathname.replace(/\.js$/, '');
    } catch (_error) {
      return '';
    }
  };

  const cartRoutes = () => {
    const routes = window.routes || {};
    return {
      add: routes.cart_add_url || '/cart/add',
      change: routes.cart_change_url || '/cart/change',
      update: routes.cart_update_url || '/cart/update',
      cart: routes.cart_url || '/cart',
    };
  };

  const isCartMutation = (resource) => {
    const path = normalizePath(resource);
    const routes = cartRoutes();
    return [routes.add, routes.change, routes.update].map(normalizePath).includes(path);
  };

  const readRequestBody = (body) => {
    if (!body) return {};
    if (typeof FormData !== 'undefined' && body instanceof FormData) return Object.fromEntries(body.entries());
    if (typeof body === 'string') {
      try {
        return JSON.parse(body);
      } catch (_error) {
        return {};
      }
    }
    return body;
  };

  const cartSectionContext = (init = {}) => {
    const body = readRequestBody(init.body);
    const rawSections = body.sections || '';
    return {
      sections: Array.isArray(rawSections)
        ? rawSections
        : String(rawSections).split(',').map((section) => section.trim()).filter(Boolean),
      sectionsUrl: body.sections_url || window.location.pathname,
    };
  };

  const requestedComponents = (formData, productContext = {}) => {
    const matches = new Map();
    if (BUNDLE_COMPONENTS[productContext.handle]) {
      matches.set(productContext.handle, {
        handle: productContext.handle,
        name: BUNDLE_COMPONENTS[productContext.handle].name,
      });
    }

    const body = readRequestBody(formData);
    const requestedItems = Array.isArray(body.items)
      ? body.items
      : [{ id: body.id, quantity: body.quantity || 1 }];
    requestedItems.forEach((item) => {
      const component = COMPONENT_BY_VARIANT_ID.get(String(item?.id || ''));
      if (component) matches.set(component.handle, component);
    });
    return [...matches.values()];
  };

  const constrainQuantityInputs = (root = document) => {
    root
      .querySelectorAll('input[name="quantity"], input[name="updates[]"], input[name^="updates["]')
      .forEach((input) => {
        input.max = '1';
        if (Number.parseInt(input.value, 10) > 1) input.value = '1';
      });
  };

  const ensureCartAdjustmentNotice = () => {
    let notice = document.getElementById('AfbCartAdjustmentMessage');
    if (notice) return notice;

    notice = document.createElement('div');
    notice.id = 'AfbCartAdjustmentMessage';
    notice.className = 'afb-cart-adjustment-message';
    notice.setAttribute('role', 'status');
    notice.setAttribute('aria-live', 'polite');
    notice.setAttribute('aria-atomic', 'true');

    const messageText = document.createElement('span');
    messageText.className = 'afb-cart-adjustment-message__text';
    notice.appendChild(messageText);
    notice.afbMessageText = messageText;

    const dismissButton = document.createElement('button');
    dismissButton.type = 'button';
    dismissButton.className = 'afb-cart-adjustment-message__dismiss';
    dismissButton.textContent = 'Dismiss';
    dismissButton.setAttribute('aria-label', 'Dismiss cart message');
    dismissButton.addEventListener('click', () => notice.classList.remove('is-visible'));
    notice.appendChild(dismissButton);
    document.body.appendChild(notice);

    if (!document.getElementById('AfbCartAdjustmentMessageStyles')) {
      const style = document.createElement('style');
      style.id = 'AfbCartAdjustmentMessageStyles';
      style.textContent = `
        .afb-cart-adjustment-message {
          position: fixed;
          z-index: 2147483647;
          top: max(1.2rem, env(safe-area-inset-top));
          left: 50%;
          display: flex;
          align-items: flex-start;
          gap: 1.2rem;
          width: min(46rem, calc(100vw - 2.4rem));
          padding: 1.4rem 1.5rem;
          border: 1px solid rgba(166, 61, 47, 0.38);
          border-radius: 1.2rem;
          background: #f6f3ec;
          color: #14261f;
          box-shadow: 0 1.2rem 3rem rgba(20, 38, 31, 0.24);
          font-family: 'Instrument Sans', 'Helvetica Neue', Arial, sans-serif;
          font-size: 1.4rem;
          line-height: 1.45;
          opacity: 0;
          transform: translate(-50%, -0.8rem);
          visibility: hidden;
          pointer-events: none;
          transition: opacity 180ms ease, transform 180ms ease, visibility 180ms ease;
        }

        .afb-cart-adjustment-message.is-visible {
          opacity: 1;
          transform: translate(-50%, 0);
          visibility: visible;
          pointer-events: auto;
        }

        .afb-cart-adjustment-message__text {
          flex: 1;
        }

        .afb-cart-adjustment-message__dismiss {
          flex: 0 0 auto;
          padding: 0;
          border: 0;
          border-bottom: 1px solid currentColor;
          background: transparent;
          color: #a63d2f;
          font: inherit;
          font-size: 1.2rem;
          cursor: pointer;
        }
      `;
      document.head.appendChild(style);
    }

    return notice;
  };

  const showCartAdjustmentMessage = (message) => {
    if (!message) return;

    const notice = ensureCartAdjustmentNotice();
    notice.afbMessageText.textContent = message;
    notice.classList.add('is-visible');
    if (noticeTimer) clearTimeout(noticeTimer);
    noticeTimer = setTimeout(() => notice.classList.remove('is-visible'), 12000);
  };

  const cartItemHandle = (item) => item.handle || item.url?.split('/products/')[1]?.split('?')[0] || '';
  const componentName = (handle) => BUNDLE_COMPONENTS[handle]?.name || handle;

  const beforeCartAdd = async (formData, productContext = {}) => {
    const components = requestedComponents(formData, productContext);
    if (components.length === 0) return { blocked: false };

    const routes = cartRoutes();
    const cartResponse = await nativeFetch(routeWithJson(routes.cart), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!cartResponse.ok) return { blocked: false };

    const cart = await cartResponse.json();
    const bundleIsPresent = (cart.items || []).some(
      (item) => cartItemHandle(item) === BUNDLE_HANDLE && item.quantity > 0
    );
    if (!bundleIsPresent) return { blocked: false, cart };

    const names = components.map((component) => component.name);
    const message = names.length === 1
      ? `Your All Three bundle already includes ${names[0]}, so we did not add it again.`
      : `Your All Three bundle already includes these packs, so we did not add them again: ${names.join(', ')}.`;
    showCartAdjustmentMessage(message);
    document.dispatchEvent(
      new CustomEvent('afb:cart-add-blocked', {
        detail: { cart, components, message },
      })
    );
    return { blocked: true, cart, components, message };
  };

  const notifyCartUpdated = (cart, updatedKeys, message = '') => {
    document.dispatchEvent(
      new CustomEvent('afb:cart-quantity-capped', {
        detail: { cart, updatedKeys, message },
      })
    );

    showCartAdjustmentMessage(message);

    setTimeout(() => {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'afb-cart-quantity-cap',
          cartData: cart,
        });
      }
      constrainQuantityInputs();
    }, 0);
  };

  const enforceCartQuantityCap = (sectionContext = {}) => {
    if (enforcementPromise) return enforcementPromise;

    enforcementPromise = (async () => {
      const routes = cartRoutes();
      const cartResponse = await nativeFetch(routeWithJson(routes.cart), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!cartResponse.ok) throw new Error('Unable to read the cart for cart-rule enforcement.');

      const cart = await cartResponse.json();
      const updates = {};
      const bundleIsPresent = (cart.items || []).some(
        (item) => cartItemHandle(item) === BUNDLE_HANDLE && item.quantity > 0
      );
      const removedComponents = [];

      for (const item of cart.items || []) {
        if (item.quantity > 1) updates[item.key] = 1;

        const handle = cartItemHandle(item);
        if (bundleIsPresent && BUNDLE_COMPONENT_HANDLES.includes(handle) && item.quantity > 0) {
          updates[item.key] = 0;
          removedComponents.push({ handle, name: componentName(handle) });
        }
      }

      const updatedKeys = Object.keys(updates);
      if (updatedKeys.length === 0) return { cart, updatedKeys, removedComponents };

      const updatePayload = { updates };
      if (sectionContext.sections?.length) {
        updatePayload.sections = sectionContext.sections;
        updatePayload.sections_url = sectionContext.sectionsUrl || window.location.pathname;
      }
      const updateResponse = await nativeFetch(routeWithJson(routes.update), {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatePayload),
      });
      if (!updateResponse.ok) throw new Error('Unable to apply the cart rules.');

      const correctedCart = await updateResponse.json();
      const removedNames = removedComponents.map((component) => component.name);
      const message = removedNames.length === 1
        ? `Your All Three bundle already includes ${removedNames[0]}, so we removed it.`
        : removedNames.length > 1
          ? `Your All Three bundle already includes these packs, so we removed them: ${removedNames.join(', ')}.`
          : '';
      notifyCartUpdated(correctedCart, updatedKeys, message);
      return { cart: correctedCart, updatedKeys, removedComponents, message };
    })().finally(() => {
      enforcementPromise = null;
    });

    return enforcementPromise;
  };

  window.afbBeforeCartAdd = beforeCartAdd;
  window.afbEnforceCartQuantityCap = async () => (await enforceCartQuantityCap()).cart;
  window.fetch = async (...args) => {
    const response = await nativeFetch(...args);

    if (response.ok && isCartMutation(args[0])) {
      try {
        const correction = await enforceCartQuantityCap(cartSectionContext(args[1]));
        const isAddRequest = normalizePath(args[0]) === normalizePath(cartRoutes().add);
        if (isAddRequest && correction.updatedKeys.length > 0 && correction.cart?.sections) {
          const originalBody = await response.clone().json();
          const responseInit = { status: response.status, headers: response.headers };
          if (response.statusText) responseInit.statusText = response.statusText;
          return new Response(
            JSON.stringify({ ...originalBody, sections: correction.cart.sections }),
            responseInit
          );
        }
      } catch (error) {
        console.error('[AFB cart rules]', error);
      }
    }

    return response;
  };

  document.addEventListener('DOMContentLoaded', () => {
    constrainQuantityInputs();
    enforceCartQuantityCap().catch((error) => console.error('[AFB cart quantity cap]', error));

    new MutationObserver(() => constrainQuantityInputs()).observe(document.body, {
      childList: true,
      subtree: true,
    });
  });
})();
